/**
 * Route guards.
 *
 * The router previously had none: every route rendered for anyone, and
 * /super-admin/dashboard was reachable exactly like /about. Protection was
 * per-page and inconsistent, so whether a route was actually gated depended on
 * whether that particular page remembered to check.
 *
 * The important detail here is the `!isLoaded` branch. Firebase resolves auth
 * asynchronously, so on a cold load `isAuthenticated` is false before it is
 * known. Redirecting in that window bounces signed-in users to /login on every
 * refresh — the classic version of this bug. We render a skeleton until auth is
 * actually known, and only then decide.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

/**
 * Gate a route on being signed in, and optionally on a role.
 *
 * @param {'super_admin'|'admin'} [role] required role; omit for any signed-in user
 * @param {React.ReactNode} [fallback] rendered while auth state is resolving
 */
export function RequireAuth({ children, role, fallback = null }) {
  const { isAuthenticated, isLoaded, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();

  // Auth not resolved yet — do NOT redirect, or every refresh kicks a
  // signed-in user out to /login.
  if (!isLoaded) {
    return fallback ?? <AuthGateSkeleton />;
  }

  if (!isAuthenticated) {
    // /auth is the canonical unauthenticated destination. /login and /register
    // are thin shims that immediately forward to /auth, so redirecting there
    // instead would add a pointless extra hop.
    //
    // search and hash are preserved: a deep link to
    // /invoices?status=open#row-123 previously came back as bare /invoices after
    // sign-in, dropping the filter and the anchor the user actually wanted.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate to={`/auth?returnUrl=${encodeURIComponent(returnTo)}`} replace />
    );
  }

  if (role === 'super_admin' && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (role === 'admin' && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Inverse guard for /login and /register: a signed-in user has no reason to see
 * them. Previously these pages read useStackAuth, a stub that always reported
 * isSignedIn: false, so a signed-in user was shown the login form again.
 */
export function RedirectIfSignedIn({ children, to = '/dashboard' }) {
  const { isAuthenticated, isLoaded } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <AuthGateSkeleton />;
  if (isAuthenticated) {
    // Honour the route the user was originally sent here from.
    return <Navigate to={location.state?.from || to} replace />;
  }
  return children;
}

function AuthGateSkeleton() {
  return (
    <div
      className="flex min-h-[50vh] items-center justify-center"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Checking your sign-in status</span>
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
    </div>
  );
}

export default RequireAuth;
